<template>
  <div class="about-page">
    <section class="about-hero">
      <div class="hero-orbit hero-orbit-one" aria-hidden="true"></div>
      <div class="hero-orbit hero-orbit-two" aria-hidden="true"></div>
      <div class="hero-inner">
        <p class="eyebrow">Meet your tutor</p>
        <h1>A tutor who remembers what <em>confusion</em> feels like.</h1>
        <p class="hero-copy">
          I’m Ali Khalil—a former SUNY Ulster peer tutor who turns accounting
          jargon into explanations you can actually use.
        </p>
        <div class="hero-proof">
          <span><strong>1:1</strong> attention</span>
          <span><strong>Zero</strong> judgment</span>
          <span><strong>Real</strong> understanding</span>
        </div>
      </div>
    </section>

    <section class="story-section">
      <div class="story-intro">
        <p class="section-kicker">The short version</p>
        <h2>I didn’t become a tutor because accounting was always easy.</h2>
      </div>
      <div class="story-copy">
        <p>
          I became one because I know the feeling of staring at debits, credits,
          and journal entries while everyone else seems to understand. Once I
          found a way to break each idea into a clear sequence, accounting
          stopped feeling like a foreign language.
        </p>
        <p>
          As a peer tutor at SUNY Ulster, I saw the same shift happen for other
          students. The right explanation—not another rushed lecture—could turn
          “I can’t do this” into an honest, earned “I get it.”
        </p>
      </div>
    </section>

    <section class="method-section">
      <div class="method-heading">
        <p class="section-kicker">How I teach</p>
        <h2>The answer matters. Your ability to find it again matters more.</h2>
      </div>

      <div class="values-grid">
        <article class="value-card value-blue">
          <span class="value-number">01</span>
          <h3>Clarity first</h3>
          <p>
            We strip away the jargon, map the logic, and use examples that make
            the concept visible.
          </p>
        </article>
        <article class="value-card value-amber">
          <span class="value-number">02</span>
          <h3>Patience always</h3>
          <p>
            Questions are welcome. We slow down, try another angle, and keep
            going until the idea lands.
          </p>
        </article>
        <article class="value-card value-mint">
          <span class="value-number">03</span>
          <h3>Independence next</h3>
          <p>
            Guided practice becomes your own repeatable process for homework,
            quizzes, and exams.
          </p>
        </article>
      </div>
    </section>

    <section class="manifesto">
      <p class="manifesto-mark" aria-hidden="true">“</p>
      <blockquote>
        My job is not to make you dependent on a tutor. It’s to help you become
        the student who knows what to do next.
      </blockquote>
      <p class="manifesto-signature">— Ali Khalil</p>
    </section>

    <section class="about-cta">
      <div>
        <p class="section-kicker">Bring the question you’re stuck on</p>
        <h2>Let’s find the explanation that clicks.</h2>
      </div>
      <router-link to="/contact" class="cta-button">
        Book a free consultation
        <span aria-hidden="true">↗</span>
      </router-link>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.about-page {
  overflow: hidden;
  background: var(--paper);
  color: var(--ink);
}

.about-hero {
  position: relative;
  min-height: 690px;
  display: grid;
  place-items: center;
  overflow: hidden;
  padding: 8rem 1.5rem 7rem;
  background:
    linear-gradient(rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    var(--ink);
  background-size: 72px 72px;
  color: white;
}

.about-hero::after {
  content: "";
  position: absolute;
  inset: auto -10% -34% 35%;
  height: 480px;
  border-radius: 50%;
  background: var(--blue);
  filter: blur(110px);
  opacity: 0.32;
}

.hero-inner {
  position: relative;
  z-index: 2;
  width: min(1040px, 100%);
}

.eyebrow,
.section-kicker {
  margin: 0 0 1.25rem;
  color: var(--amber);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

h1 {
  max-width: 960px;
  margin: 0;
  font-size: clamp(3.25rem, 8vw, 7.6rem);
  font-weight: 800;
  letter-spacing: -0.07em;
  line-height: 0.91;
}

h1 em {
  position: relative;
  color: var(--amber);
  font-family: Georgia, "Times New Roman", serif;
  font-weight: 400;
}

.hero-copy {
  max-width: 650px;
  margin: 2.5rem 0 3rem;
  color: #cbd1df;
  font-size: clamp(1.08rem, 2vw, 1.3rem);
  line-height: 1.7;
}

.hero-proof {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.hero-proof span {
  padding: 0.8rem 1.05rem;
  border: 1px solid rgba(255, 255, 255, 0.17);
  border-radius: 999px;
  color: #dfe4ef;
  font-size: 0.9rem;
}

.hero-proof strong {
  color: white;
}

.hero-orbit {
  position: absolute;
  border: 1px solid rgba(255, 189, 89, 0.4);
  border-radius: 50%;
}

.hero-orbit-one {
  width: 470px;
  height: 470px;
  top: -180px;
  right: -110px;
}

.hero-orbit-two {
  width: 290px;
  height: 290px;
  top: -90px;
  right: -20px;
}

.story-section,
.method-section,
.about-cta {
  width: min(1180px, calc(100% - 3rem));
  margin: 0 auto;
}

.story-section {
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: clamp(3rem, 8vw, 8rem);
  padding: clamp(6rem, 12vw, 10rem) 0;
}

.story-intro h2,
.method-heading h2,
.about-cta h2 {
  margin: 0;
  font-size: clamp(2.4rem, 5vw, 4.6rem);
  letter-spacing: -0.055em;
  line-height: 1.02;
}

.story-copy {
  padding-top: 2.6rem;
}

.story-copy p {
  margin: 0 0 1.5rem;
  color: #4a5162;
  font-size: 1.08rem;
  line-height: 1.85;
}

.story-copy p:first-child::first-letter {
  float: left;
  margin: 0.09em 0.12em 0 0;
  color: var(--blue);
  font-size: 4.8rem;
  font-weight: 800;
  line-height: 0.75;
}

.method-section {
  padding-bottom: clamp(6rem, 10vw, 9rem);
}

.method-heading {
  display: grid;
  grid-template-columns: 0.45fr 1fr;
  align-items: start;
  gap: 2rem;
  margin-bottom: 3.5rem;
}

.method-heading .section-kicker {
  color: var(--blue);
  padding-top: 0.8rem;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border: 1px solid #dfe2eb;
  border-radius: 26px;
  overflow: hidden;
  background: white;
  box-shadow: 0 24px 80px rgba(10, 16, 32, 0.07);
}

.value-card {
  min-height: 360px;
  padding: clamp(2rem, 4vw, 3.4rem);
}

.value-card + .value-card {
  border-left: 1px solid #dfe2eb;
}

.value-number {
  display: inline-grid;
  width: 50px;
  height: 50px;
  place-items: center;
  margin-bottom: 6rem;
  border-radius: 50%;
  background: var(--ink);
  color: white;
  font-size: 0.78rem;
  font-weight: 800;
}

.value-card h3 {
  margin: 0 0 1rem;
  font-size: 1.55rem;
  letter-spacing: -0.035em;
}

.value-card p {
  margin: 0;
  color: #5b6272;
  line-height: 1.7;
}

.value-blue {
  background: linear-gradient(145deg, rgba(49, 87, 246, 0.09), white 52%);
}

.value-amber {
  background: linear-gradient(145deg, rgba(255, 189, 89, 0.16), white 52%);
}

.value-mint {
  background: linear-gradient(145deg, rgba(24, 201, 154, 0.11), white 52%);
}

.manifesto {
  position: relative;
  padding: clamp(6rem, 12vw, 10rem) 1.5rem;
  overflow: hidden;
  background: var(--blue);
  color: white;
  text-align: center;
}

.manifesto-mark {
  position: absolute;
  top: -1.5rem;
  left: 50%;
  margin: 0;
  color: rgba(255, 255, 255, 0.12);
  font-family: Georgia, serif;
  font-size: 24rem;
  line-height: 1;
  transform: translateX(-50%);
}

.manifesto blockquote {
  position: relative;
  width: min(970px, 100%);
  margin: 0 auto 2rem;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(2.2rem, 5vw, 5rem);
  font-weight: 400;
  letter-spacing: -0.045em;
  line-height: 1.08;
}

.manifesto-signature {
  position: relative;
  margin: 0;
  color: #dbe2ff;
  font-weight: 700;
}

.about-cta {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 3rem;
  padding: clamp(5rem, 10vw, 8rem) 0;
}

.about-cta .section-kicker {
  color: var(--blue);
}

.cta-button {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 1rem;
  padding: 1.05rem 1.35rem;
  border-radius: 999px;
  background: var(--ink);
  color: white;
  font-weight: 800;
  transition: transform 180ms ease, background 180ms ease;
}

.cta-button span {
  font-size: 1.2rem;
}

.cta-button:hover {
  background: var(--blue);
  transform: translateY(-2px);
}

@media (max-width: 820px) {
  .about-hero {
    min-height: 610px;
    padding-top: 6.5rem;
  }

  .story-section,
  .method-heading {
    grid-template-columns: 1fr;
  }

  .story-copy {
    padding-top: 0;
  }

  .values-grid {
    grid-template-columns: 1fr;
  }

  .value-card {
    min-height: 290px;
  }

  .value-card + .value-card {
    border-top: 1px solid #dfe2eb;
    border-left: 0;
  }

  .value-number {
    margin-bottom: 3.8rem;
  }

  .about-cta {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 540px) {
  .about-hero {
    min-height: 560px;
  }

  .story-section,
  .method-section,
  .about-cta {
    width: min(100% - 2rem, 1180px);
  }

  .hero-proof {
    align-items: flex-start;
    flex-direction: column;
  }

  h1 {
    font-size: clamp(3rem, 15vw, 4.6rem);
  }
}
</style>
